import { useState } from "react";

import { useTranslation } from "react-i18next";

import { BellIcon } from "lucide-react";

import { Button } from "simplydo/react/ui/button";
import { H3 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Message } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import { setupNotifications } from "utils/pushMessaging";
import util from "utils/utils";

type PushSetupModalProps = { open: boolean; setOpen: (open: boolean) => void };

const PushSetupModal = ({ open, setOpen }: PushSetupModalProps) => {
  const [pushState, setPushState] = useState<
    typeof Notification.permission | "loading"
  >(("Notification" in window && Notification.permission) || "denied");
  const { t } = useTranslation();

  return (
    <Modal open={open}>
      <ModalHeader>
        <Icon icon={BellIcon} />
        Enable offline notifications
      </ModalHeader>
      <ModalContent>
        <H3>Why?</H3>
        <p>
          Right now we can only send you updates while you are working within
          the {util.appName()} platform. If you want to receive notifications
          instantly and even when you are offline, you can enable push
          notifications now. We will only ever send you updates for content that
          is relevant to you. Additionally, you can choose to limit the kind of
          notifications you would like to receive in your settings, for this
          navigate to <strong>Account {"->"} Notifications</strong>.
        </p>

        <H3>How to?</H3>
        <p>
          Click the button below to get started. This will show you a popup at
          the top of your browser window. Press &quot;Allow&quot; (or
          equivalent) and we will be able to send you push notifications for any
          changes of your {t("generic.ideas")} and challenges in your
          organisation.
        </p>
        <Button
          disabled={pushState === "granted" || pushState === "denied"}
          loading={pushState === "loading"}
          onClick={() => {
            setPushState("loading");
            setupNotifications().finally(() => {
              setPushState(Notification.permission);
            });
          }}
          color="primary"
        >
          Set up notifications now
        </Button>
        {pushState === "granted" ? (
          <Message color="green">Notifications are now enabled!</Message>
        ) : null}
        {pushState === "denied" ? (
          <Message color="orange">
            You have declined to receive push notifications. If you change your
            mind you can always change this in your browser settings.
          </Message>
        ) : null}
      </ModalContent>
      <ModalActions>
        <Button
          onClick={() => {
            if (pushState === "default") {
              if (
                confirm(
                  "You did not enable push notifications. Are you sure you want to leave them disabled?",
                )
              ) {
                setOpen(false);
              }
            } else {
              setOpen(false);
            }
          }}
        >
          Done
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default PushSetupModal;
